import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  ButtonGroup,
  TextField,
  IconButton,
  Input,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { Product } from '../types/marketing';
import * as mockDataService from '../services/mockData';
import { deleteProductAsync, updateProductAsync } from '../redux/slices/productSlice';
import LeadProfile from '../components/LeadProfile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  source: string;
  status: string;
  notes?: string;
  lastContactDate?: string;
  productInterest?: string;
  createdAt?: string;
  interestLevel?: 'alto' | 'médio' | 'baixo';
}

interface ProductFormData {
  id: string;
  code: string;
  type: 'produto' | 'servico';
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  videoUrl: string;
  category: string;
}

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedLeads, setRelatedLeads] = useState<Lead[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const leads = useSelector((state: RootState) => state.leads.items);
  const [formData, setFormData] = useState<ProductFormData>({
    id: product?.id || '',
    code: product?.code || '',
    type: product?.type || 'produto',
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || 0,
    imageUrl: product?.imageUrl || '',
    videoUrl: product?.videoUrl || '',
    category: product?.category || ''
  });
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [isLeadProfileOpen, setIsLeadProfileOpen] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const productData = await mockDataService.getProduct(id);
        
        if (productData) {
          setProduct(productData);
          
          // Inicializar o formulário de edição com os dados do produto
          setFormData({
            id: productData.id,
            code: productData.code,
            type: productData.type,
            name: productData.name,
            description: productData.description,
            price: productData.price,
            imageUrl: productData.imageUrl || '',
            videoUrl: productData.videoUrl || '',
            category: productData.category || '',
          });
          
          // Filtrar leads interessados neste produto
          const filteredLeads = leads.filter(lead => lead.productInterest === id);
          setRelatedLeads(filteredLeads);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar produto:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProduct();
  }, [id, leads]);

  const handleBack = () => {
    navigate('/produtos');
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleEditClick = () => {
    setOpenEditDialog(true);
    setPreviewImage(product?.imageUrl || null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Verificar o tipo do arquivo
      if (!file.type.match('image.*')) {
        setSnackbarMessage('Por favor, selecione apenas arquivos de imagem.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      // Verificar o tamanho do arquivo (limite de 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbarMessage('A imagem deve ter no máximo 5MB.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      
      setSelectedImage(file);
      
      // Criar URL para preview da imagem
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setPreviewImage(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.price || !formData.id) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      // Se uma nova imagem foi selecionada, em um ambiente real faríamos upload para um servidor
      // e obteríamos a URL. Como é um mock, vamos simular usando o preview como URL
      let imageUrl = formData.imageUrl;
      if (selectedImage && previewImage) {
        // Em um ambiente real, aqui faríamos o upload da imagem para um servidor
        // e obteríamos a URL real. Para o mock, usamos o preview como URL
        imageUrl = previewImage;
      }
      
      const updatedProduct = {
        id: formData.id,
        code: formData.code,
        type: formData.type,
        name: formData.name,
        description: formData.description,
        price: formData.price,
        imageUrl: imageUrl,
        videoUrl: formData.videoUrl,
        category: formData.category,
      };
      
      const result = await dispatch(updateProductAsync(updatedProduct) as any);
      if (result.payload) {
        setProduct(result.payload);
        setOpenEditDialog(false);
        setSnackbarMessage('Produto atualizado com sucesso!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Limpar a imagem selecionada após o envio
        setSelectedImage(null);
        setPreviewImage(null);
      }
    } catch (error) {
      setSnackbarMessage('Erro ao atualizar produto. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!formData.id) return;
    
    try {
      await dispatch(deleteProductAsync(formData.id) as any);
      setSnackbarMessage('Produto excluído com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Redirecionar para a página de produtos após um breve delay
      setTimeout(() => {
        navigate('/produtos');
      }, 1500);
    } catch (error) {
      setSnackbarMessage('Erro ao excluir produto. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setOpenDeleteDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleLeadClick = (lead: Lead) => {
    // Garantir que as datas estejam no formato ISO e adicionar campos obrigatórios
    const formattedLead = {
      ...lead,
      createdAt: lead.createdAt || new Date().toISOString(),
      lastContact: lead.lastContactDate || new Date().toISOString(),
      interestLevel: lead.interestLevel || 'médio',
      status: lead.status || 'novo',
      notes: lead.notes || 'Sem observações',
      phone: lead.phone || 'Não informado'
    };
    setSelectedLead(formattedLead);
    setIsLeadProfileOpen(true);
  };

  const handleCloseLeadProfile = () => {
    setIsLeadProfileOpen(false);
    setSelectedLead(null);
  };

  const handleLeadDelete = (leadId: string) => {
    // Atualizar a lista de leads relacionados
    setRelatedLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
    
    // Mostrar mensagem de sucesso
    setSnackbarMessage('Lead excluído com sucesso!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg">
        <Box py={4} textAlign="center">
          <Typography variant="h5" color="error">
            Produto não encontrado
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Voltar para Produtos
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" alignItems="center" mb={4}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Box flexGrow={1}>
            <Typography variant="h4" component="h1">
              {product.name}
            </Typography>
          </Box>
          <ButtonGroup>
            <Button
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
            >
              Editar
            </Button>
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              variant="contained"
              color="error"
            >
              Excluir
            </Button>
          </ButtonGroup>
        </Box>

        <Grid container spacing={4}>
          {product.videoUrl ? (
            // Layout com vídeo - dois cards lado a lado
            <>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <Box sx={{ height: '300px', position: 'relative' }}>
                    <CardMedia
                      component="img"
                      sx={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover'
                      }}
                      image={product.imageUrl || 'https://via.placeholder.com/400'}
                      alt={product.name}
                    />
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Foto do Produto
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button 
                        size="small" 
                        startIcon={<FullscreenIcon />}
                        onClick={() => {
                          window.open(product.imageUrl, '_blank');
                        }}
                      >
                        Ver em Tela Cheia
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      pb: 1
                    }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Vídeo do Produto
                      </Typography>
                      <Chip 
                        label="MP4" 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '300px',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 3,
                        backgroundColor: '#000',
                        '&:hover .video-controls': {
                          opacity: 1
                        }
                      }}
                    >
                      <Box
                        component="video"
                        controls
                        width="100%"
                        height="100%"
                        sx={{
                          borderRadius: 1,
                          display: 'block',
                          objectFit: 'cover'
                        }}
                        poster={product.imageUrl || undefined}
                      >
                        <source src={product.videoUrl} type="video/mp4" />
                        Seu navegador não suporta o elemento de vídeo.
                      </Box>
                      <Box 
                        className="video-controls"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                          p: 1,
                          opacity: 0.7,
                          transition: 'opacity 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" color="white">
                          Clique para reproduzir o vídeo
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Vídeo demonstrativo do produto
                      </Typography>
                      <Box>
                        <Button 
                          size="small" 
                          startIcon={<PlayArrowIcon />}
                          onClick={() => {
                            const videoElement = document.querySelector('video');
                            if (videoElement) {
                              videoElement.play();
                            }
                          }}
                          sx={{ mr: 1 }}
                        >
                          Reproduzir
                        </Button>
                        <Button
                          size="small"
                          startIcon={<FullscreenIcon />}
                          onClick={() => {
                            const videoElement = document.querySelector('video');
                            if (videoElement) {
                              if (videoElement.requestFullscreen) {
                                videoElement.requestFullscreen();
                              } else if ((videoElement as any).webkitRequestFullscreen) {
                                (videoElement as any).webkitRequestFullscreen();
                              } else if ((videoElement as any).msRequestFullscreen) {
                                (videoElement as any).msRequestFullscreen();
                              }
                            }
                          }}
                          sx={{ mr: 1 }}
                        >
                          Tela Cheia
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ShareIcon />}
                          onClick={() => setOpenShareDialog(true)}
                        >
                          Compartilhar
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </>
          ) : (
            // Layout sem vídeo - apenas a imagem ocupando toda a largura
            <Grid item xs={12}>
              <Card>
                <Box sx={{ height: '300px', position: 'relative' }}>
                  <CardMedia
                    component="img"
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover'
                    }}
                    image={product.imageUrl || 'https://via.placeholder.com/400'}
                    alt={product.name}
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Foto do Produto
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button 
                      size="small" 
                      startIcon={<FullscreenIcon />}
                      onClick={() => {
                        window.open(product.imageUrl, '_blank');
                      }}
                    >
                      Ver em Tela Cheia
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Informações do Produto */}
          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="overline" color="text.secondary" sx={{ mr: 1 }}>
                    {product.code}
                  </Typography>
                  <Chip 
                    label={product.type === 'produto' ? 'Produto' : 'Serviço'}
                    size="small" 
                    color={product.type === 'produto' ? 'primary' : 'secondary'}
                  />
                </Box>
                <Typography variant="h4" gutterBottom>
                  {product.name}
                </Typography>
                
                <Chip 
                  label={product.category} 
                  color="primary" 
                  variant="outlined" 
                  sx={{ mb: 2 }}
                />
                
                <Typography variant="h5" color="primary" gutterBottom>
                  R$ {product.price.toFixed(2)}
                </Typography>
                
                <Typography variant="body1" paragraph>
                  {product.description}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Box mt={6}>
          <Typography variant="h5" gutterBottom>
            Leads Interessados ({relatedLeads.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {relatedLeads.length > 0 ? (
            <Grid container spacing={2}>
              {relatedLeads.map((lead) => (
                <Grid item xs={12} sm={6} md={4} key={lead.id}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                    onClick={() => handleLeadClick(lead)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {lead.name}
                    </Typography>
                    <Typography variant="body2">
                      Email: {lead.email}
                    </Typography>
                    <Typography variant="body2">
                      Telefone: {lead.phone || '-'}
                    </Typography>
                    <Box mt={1}>
                      <Chip 
                        label={lead.status} 
                        color={
                          lead.status === 'Convertido' ? 'success' :
                          lead.status === 'Qualificado' ? 'primary' :
                          lead.status === 'Contatado' ? 'warning' :
                          lead.status === 'Perdido' ? 'error' : 'info'
                        }
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Nenhum lead demonstrou interesse neste produto ainda.
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o produto "{product.name}"? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Produto</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Código: {formData.code}
            </Typography>
          </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'produto' | 'servico' })}
              label="Tipo"
            >
              <MenuItem value="produto">Produto</MenuItem>
              <MenuItem value="servico">Serviço</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            id="edit-imageUrl"
            name="imageUrl"
            label="URL da Imagem (opcional se carregar arquivo)"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.imageUrl}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Vídeo do Produto
            </Typography>
            <TextField
              margin="dense"
              id="edit-videoUrl"
              name="videoUrl"
              label="URL do Vídeo (opcional)"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.videoUrl}
              onChange={handleEditChange}
              sx={{ mb: 1 }}
              helperText="Insira a URL do vídeo em formato MP4 ou faça upload de um arquivo"
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<VideoLibraryIcon />}
                sx={{ mr: 1 }}
              >
                Upload de Vídeo
                <input
                  type="file"
                  hidden
                  accept="video/mp4,video/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      // Em um ambiente real, aqui faríamos o upload do arquivo para um servidor
                      // e obteríamos a URL. Como é um mock, vamos simular usando um objeto URL
                      const videoUrl = URL.createObjectURL(file);
                      setFormData({ ...formData, videoUrl });
                      setSnackbarMessage('Vídeo carregado com sucesso!');
                      setSnackbarSeverity('success');
                      setSnackbarOpen(true);
                    }
                  }}
                />
              </Button>
              {formData.videoUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => {
                    setFormData({ ...formData, videoUrl: '' });
                    setSnackbarMessage('Vídeo removido');
                    setSnackbarSeverity('info');
                    setSnackbarOpen(true);
                  }}
                >
                  Remover
                </Button>
              )}
            </Box>
          </Box>
          {formData.videoUrl && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Prévia do Vídeo
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 1,
                  backgroundColor: '#000',
                  maxHeight: '200px'
                }}
              >
                <Box
                  component="video"
                  controls
                  width="100%"
                  sx={{
                    display: 'block'
                  }}
                  poster={formData.imageUrl || undefined}
                >
                  <source src={formData.videoUrl} type="video/mp4" />
                  Seu navegador não suporta o elemento de vídeo.
                </Box>
              </Box>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            id="edit-name"
            name="name"
            label="Nome do Produto"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="edit-description"
            name="description"
            label="Descrição"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="edit-price"
            name="price"
            label="Preço"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.price}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="edit-category"
            name="category"
            label="Categoria"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.category}
            onChange={handleEditChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {selectedLead && (
        <LeadProfile
          lead={selectedLead}
          open={isLeadProfileOpen}
          onClose={handleCloseLeadProfile}
          onDelete={handleLeadDelete}
        />
      )}

      <Dialog open={openShareDialog} onClose={() => setOpenShareDialog(false)}>
        <DialogTitle>Compartilhar Vídeo</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Compartilhe o vídeo deste produto com seus clientes:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Link do Vídeo"
              value={product.videoUrl || ''}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton 
                    onClick={() => {
                      if (product.videoUrl) {
                        navigator.clipboard.writeText(product.videoUrl);
                        setSnackbarMessage('Link copiado para a área de transferência!');
                        setSnackbarSeverity('success');
                        setSnackbarOpen(true);
                      }
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={() => {
                  if (product.videoUrl) {
                    window.open(`https://wa.me/?text=Confira este produto: ${product.name} - ${product.videoUrl}`, '_blank');
                  }
                }}
              >
                WhatsApp
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => {
                  if (product.videoUrl) {
                    window.open(`mailto:?subject=Confira este produto: ${product.name}&body=${product.videoUrl}`, '_blank');
                  }
                }}
              >
                Email
              </Button>
              <Button
                variant="outlined"
                startIcon={<FacebookIcon />}
                onClick={() => {
                  if (product.videoUrl) {
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(product.videoUrl)}`, '_blank');
                  }
                }}
              >
                Facebook
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenShareDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductDetailPage; 